import { isFeatureEnable } from 'common/js/features-enabling';
import { logMessage } from 'common/js/sentry';
import { sendAnalyticsTrack } from 'common/js/TrackAnalyticsHelper';
import { installShortcut } from 'common/js/shortcut-utils';
import {
  UserClickHandler,
  UserClickHandlerProps
} from 'common/stores/handlers/UserClickHandler';
import { MonitoringStore } from 'common/stores/MonitoringStore';
import { triggerOgyOnAdClicked } from 'common/js/mraid-utils';
import { onFormatClick } from 'common/js/format-utils';
import { isWeb } from 'common/js/environment-utils';

export class ImageDisplayUserClickHandler extends UserClickHandler {
  monitoringStore: MonitoringStore;

  constructor(
    props: UserClickHandlerProps & { monitoringStore: MonitoringStore }
  ) {
    super(props);
    this.monitoringStore = props.monitoringStore;
  }

  handle(e: ClickEvent) {
    if (!this.isClickAuthorized(e) && !window.isOgyPreview) {
      return;
    }

    this.trackFormatInteraction(e);
    this.trackCreativeClientClick(e);
    this.trackViewableState();

    this.finalizeClick(e);
  }

  private isClickAuthorized(e: ClickEvent) {
    const { ignoreClicksBefore } = this.formatConfig;
    const { viewableDuration } = this.uiTimerStore;

    if (ignoreClicksBefore > 0 && viewableDuration < ignoreClicksBefore) {
      console.log(
        `Ignored click event. Interaction happened ${viewableDuration}ms after render but ignore_clicks_before is set to ${ignoreClicksBefore}ms`
      );
      this.handleCreativeIgnoredClick(e);
      sendAnalyticsTrack(e, 'ignored_format_interaction', false);
      return false;
    }

    if (!isFeatureEnable('impressionFormatTrack')) {
      logMessage(
        'impressionFormatTrack feature is disabled. Bypassing impression track verification before click'
      );
      return true;
    }

    const { impressionFormatSent } = this.analyticsStore;

    if (!impressionFormatSent) {
      logMessage(`No Impression format occured before click`);
      this.monitoringStore.sendLog('No impression format occured before click');
      this.handleClickWithoutImpression(e);
    }

    return impressionFormatSent;
  }

  private trackFormatInteraction(e: ClickEvent) {
    const { impressionFormatSent } = this.analyticsStore;

    if (!impressionFormatSent) {
      return;
    }

    sendAnalyticsTrack(e, 'format_interaction', true);
    installShortcut(this.formatOptions, this.formatConfig).catch(err => {
      console.error(err);
    });
  }

  private trackCreativeClientClick(e: ClickEvent) {
    this.analyticsStore.sendCreativeTrack('client_click', e);
  }

  private trackViewableState() {
    const { isViewable } = this.mraidStore;

    if (!isViewable) {
      return;
    }

    this.analyticsStore.sendCreativeTrack('viewable');
  }

  private finalizeClick(e: ClickEvent) {
    const { impressionFormatSent } = this.analyticsStore;

    if (!impressionFormatSent) {
      logMessage(`Finalizing click without impression`);
      this.monitoringStore.sendLog('Finalizing click without impression');
    }

    triggerOgyOnAdClicked();
    onFormatClick(this.formatOptions, this.formatConfig.clickUrl);

    if (isWeb()) {
      this.setUserHasClicked();
    }
  }

  private handleClickWithoutImpression(e: ClickEvent) {
    this.trackCreativeClientClick(e);
    this.trackViewableState();
  }
}
